import {
  CalendarFilterBar,
  CalendarGrid,
  CalendarOptionsDrawer,
  CalendarPageEyebrow,
  CalendarPageTitleBlock,
  CalendarPageNav,
  CalendarShellHeader,
  CalendarTopBarActions,
  MonthGrid,
  MonthPeekModal,
  WeekGrid,
} from '@elva/ui';
import { useWorkspaceLabel } from '@elva/platform';
import { useAccountUser } from '@/app/layouts/useAccountUser';
import { ElvaSectionState } from '@/components/ElvaSectionState';
import { AppointmentDetailConnected } from '../../appointments/components/AppointmentDetailConnected';
import { weekAppointmentToAppointment } from '../adapters/calendarAdapters';
import { useCalendarState } from '../hooks/useCalendarState';

export function CalendarPage() {
  const workspaceLabel = useWorkspaceLabel();
  const accountUser = useAccountUser();
  const {
    view,
    setView,
    optionsOpen,
    setOptionsOpen,
    selected,
    setSelected,
    monthPeek,
    setMonthPeek,
    dayFilters,
    weekFilters,
    monthFilters,
    filters,
    filterChips,
    filterCounts,
    removeFilterChip,
    clearAllFilters,
    large,
    setLarge,
    viewMode,
    setViewMode,
    timeDensity,
    setTimeDensity,
    productionThreshold,
    setProductionThreshold,
    resetDayOptions,
    appointments,
    weekAppointments,
    weekDays,
    weekTitleRange,
    weekHeaderStats,
    dayHeaderStats,
    dayPageTitle,
    goPrev,
    goNext,
    goToday,
    goToDate,
    dayDate,
    monthGridData,
    monthGridDataByDate,
    monthGridCells,
    monthToday,
    monthHeaderStats,
    monthTitle,
    dayProviders,
    dayProviderOptions,
    dayOperatoryOptions,
    hiddenProviderIds,
    hiddenOperatoryIds,
    toggleProvider,
    toggleOperatory,
    isLoadingDay,
    isLoadingWeek,
    isLoadingMonth,
    isErrorDay,
    isErrorWeek,
    isErrorMonth,
  } = useCalendarState();

  const isWeek = view === 'Week';
  const isMonth = view === 'Month';
  const sectionLoading = isMonth ? isLoadingMonth : isWeek ? isLoadingWeek : isLoadingDay;
  const sectionError = isMonth ? isErrorMonth : isWeek ? isErrorWeek : isErrorDay;

  return (
    <>
      <CalendarShellHeader
        eyebrow={
          <CalendarPageEyebrow
            workspaceLabel={workspaceLabel}
            {...(isWeek
              ? { label: `Next 7 days · ${workspaceLabel}` }
              : isMonth
                ? { label: `This month · ${workspaceLabel}` }
                : {})}
          />
        }
        left={
          <CalendarPageTitleBlock
            {...(isWeek && weekTitleRange
              ? {
                  weekRange: weekTitleRange,
                  weekStats: weekHeaderStats,
                }
              : isMonth
                ? {
                    dayTitle: monthTitle.month,
                    daySubtitle: monthTitle.year ? ` ${monthTitle.year}` : '',
                    monthStats: monthHeaderStats,
                  }
                : {
                    dayTitle: dayPageTitle.dayTitle,
                    daySubtitle: dayPageTitle.daySubtitle,
                    dayStats: dayHeaderStats,
                  })}
          />
        }
        actions={
          <CalendarTopBarActions
            optionsBadge={filterChips.length}
            onOptions={() => {
              setOptionsOpen(true);
            }}
          />
        }
        nav={
          <CalendarPageNav
            view={view}
            onViewChange={setView}
            onPrev={goPrev}
            onNext={goNext}
            onToday={goToday}
          />
        }
        user={accountUser}
      />
      <CalendarFilterBar
        chips={filterChips}
        onRemove={removeFilterChip}
        onClearAll={clearAllFilters}
        {...(filterCounts ?? {})}
      />
      <div className="flex min-h-0 flex-1 gap-5 overflow-hidden px-[30px] pb-[22px]">
        <ElvaSectionState
          isLoading={sectionLoading}
          isError={sectionError}
          className="flex min-h-0 flex-1 gap-5 overflow-hidden"
        >
          {isMonth ? (
            <MonthGrid
              year={monthToday.year}
              month={monthToday.month}
              data={monthGridData}
              {...(monthGridCells.length > 0
                ? { cells: monthGridCells, dataByDate: monthGridDataByDate }
                : {})}
              today={monthToday}
              highlightFilter={monthFilters.highlight}
              heatmap={monthFilters.heatmap}
              hiddenCellParts={monthFilters.hiddenCellParts}
              onSelectDay={(cell, rec) => {
                setMonthPeek({ cell, rec });
              }}
            />
          ) : isWeek ? (
            <WeekGrid
              days={weekDays}
              appointments={weekAppointments}
              onSelectAppointment={(appt) => {
                setSelected(weekAppointmentToAppointment(appt));
              }}
            />
          ) : (
            <CalendarGrid
              appointments={appointments}
              {...(dayProviders.length > 0 ? { providers: dayProviders } : {})}
              large={large}
              timeDensity={timeDensity}
              onSelectAppointment={(appt) => {
                setSelected(appt);
              }}
            />
          )}
        </ElvaSectionState>
      </div>
      <CalendarOptionsDrawer
        open={optionsOpen}
        onClose={() => {
          setOptionsOpen(false);
        }}
        quick={isMonth ? monthFilters.highlight : isWeek ? weekFilters.readiness : dayFilters.quick}
        onQuickChange={(v) => {
          if (isMonth) filters.setMonthHighlight(v);
          else if (isWeek) filters.setWeekReadiness(v);
          else filters.setQuick(v);
        }}
        large={large}
        onLargeChange={(v) => {
          setLarge(v);
        }}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        timeDensity={timeDensity}
        onTimeDensityChange={setTimeDensity}
        productionThreshold={productionThreshold}
        onProductionThresholdChange={setProductionThreshold}
        providers={dayProviderOptions}
        hiddenProviders={hiddenProviderIds}
        onToggleProvider={toggleProvider}
        onResetProviders={() => {
          filters.clearDaySection('providers');
        }}
        hiddenStatuses={dayFilters.hiddenStatuses}
        onToggleStatus={filters.toggleStatus}
        onResetStatus={() => {
          filters.clearDaySection('status');
        }}
        operatories={dayOperatoryOptions}
        hiddenOperatories={hiddenOperatoryIds}
        onToggleOperatory={toggleOperatory}
        onResetOperatories={() => {
          filters.clearDaySection('operatories');
        }}
        selectedFlags={dayFilters.flags}
        onToggleFlag={filters.toggleFlag}
        onResetFlags={() => {
          filters.clearDaySection('flags');
        }}
        {...(filterCounts ? { resultSummary: filterCounts } : {})}
        onResetDayOptions={resetDayOptions}
        {...(isWeek ? { onReset: filters.resetWeekFilters } : {})}
        {...(isMonth ? { onReset: filters.resetMonthFilters } : {})}
        weekHiddenStatuses={weekFilters.hiddenStatuses}
        onToggleWeekStatus={filters.toggleWeekStatus}
        weekRangeDays={weekFilters.rangeDays}
        onWeekRangeChange={filters.setWeekRange}
        monthHeatmap={monthFilters.heatmap}
        onMonthHeatmapChange={filters.setMonthHeatmap}
        monthHiddenCellParts={monthFilters.hiddenCellParts}
        onToggleMonthCellPart={filters.toggleMonthCellPart}
        weekMode={isWeek}
        monthMode={isMonth}
      />
      <AppointmentDetailConnected
        appointment={selected}
        onClose={() => {
          setSelected(null);
        }}
      />
      <MonthPeekModal
        peek={monthPeek}
        onClose={() => {
          setMonthPeek(null);
        }}
        onOpenDay={() => {
          const date = monthPeek?.cell.date;
          if (date) goToDate(date);
          setMonthPeek(null);
          setView('Day');
        }}
      />
    </>
  );
}
